/**
 * 分析结论头部组件 - 精简布局
 * 去除重复眉题，优化病名与状态信息排布
 */

import { useTranslation } from "react-i18next";
import { AlertTriangle, Activity, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

import type { AnalysisResult, RiskLevel } from "@/types/care-plan";

interface DiagnosisHeaderProps {
  result?: AnalysisResult;
  imageUrl?: string;
  currentDay?: number;
  totalDays?: number;
  className?: string;
}

export function DiagnosisHeader({
  result,
  imageUrl,
  currentDay = 1,
  totalDays = 7,
  className,
}: DiagnosisHeaderProps) {
  const { t } = useTranslation("assessments");

  if (!result) return null;

  const progress = Math.max(
    0,
    Math.round(((currentDay - 1) / totalDays) * 100),
  );
  const remaining = totalDays - currentDay + 1;

  return (
    <Card
      className={cn(
        "border bg-card/80 backdrop-blur-sm overflow-hidden",
        className,
      )}
    >
      <div className="flex items-stretch">
        {/* 左侧：核心信息 */}
        <div className="flex-1 p-5 min-w-0 space-y-3">
          {/* 第一行：病名 + 风险徽章 */}
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight leading-tight">
              {result.condition_name_display}
            </h1>
            <RiskLevelBadge level={result.risk_level} />
          </div>

          {/* 第二行：状态信息 */}
          <div className="flex items-center gap-3">
            {/* 治疗中状态 */}
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-sm font-medium text-foreground">
                {t("carePlan.inTreatment", "In Treatment")}
              </span>
            </div>

            <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />

            {/* 剩余天数 */}
            <span className="text-sm text-muted-foreground">
              {t("carePlan.daysRemaining", {
                count: remaining,
                defaultValue: "{{count}} days remaining",
              })}
            </span>
          </div>
        </div>

        {/* 右侧：环形进度 */}
        <div className="flex items-center justify-center px-6 border-l bg-muted/5">
          <div className="relative w-16 h-16">
            {/* SVG 环形进度条 */}
            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
              {/* 背景圈 */}
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                className="text-muted/20"
              />
              {/* 进度圈 */}
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                strokeDasharray={`${(progress / 100) * 175.9} 175.9`}
                className="text-primary transition-all duration-500"
                strokeLinecap="round"
              />
            </svg>

            {/* 中心百分比 */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-bold text-foreground tabular-nums">
                {progress}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * 风险等级徽章
 */
interface RiskLevelBadgeProps {
  level: RiskLevel;
  className?: string;
}

export function RiskLevelBadge({ level, className }: RiskLevelBadgeProps) {
  const { t } = useTranslation("assessments");

  const config = {
    low: {
      icon: Shield,
      className: "border-emerald-500/50 bg-emerald-500/15 text-emerald-500",
    },
    medium: {
      icon: Activity,
      className: "border-amber-500/50 bg-amber-500/15 text-amber-500",
    },
    high: {
      icon: AlertTriangle,
      className: "border-orange-500/50 bg-orange-500/15 text-orange-500",
    },
  };

  const { icon: Icon, className: badgeClassName } = config[level];

  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1.5 font-semibold shrink-0 px-2.5 py-1 text-xs",
        badgeClassName,
        className,
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {t(`carePlan.riskLevel.${level}`)}
    </Badge>
  );
}

export default DiagnosisHeader;
